import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Calendar,
  CheckCircle2,
  ChevronRight,
  FileText,
  HelpCircle,
  User,
} from "lucide-react";
import { PageShell } from "@/components/page-shell";
import { ProgressRing } from "@/components/progress-ring";
import { type Task, tasksQueryOptions } from "@/lib/data";
import { requireSession } from "@/lib/session";
import { getTaskDefinition } from "@/lib/task-definitions";
import { getTaskStatusLabel, isClosedTask } from "@/lib/task-progress";
import { useOrganization } from "@/lib/use-organization";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/steps")({
  component: TasksPage,
  beforeLoad: requireSession,
});

function TasksPage() {
  const organization = useOrganization();
  const { data, isLoading } = useQuery(tasksQueryOptions);

  const tasks = data?.tasks ?? [];
  const completedCount = tasks.filter(isClosedTask).length;
  const totalCount = tasks.length;
  const pct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allDone = totalCount > 0 && completedCount === totalCount;

  return (
    <PageShell showSignOut>
      <div className="mb-7 flex items-start gap-5">
        <ProgressRing value={pct} size={76} />
        <div className="flex-1 pt-1">
          <p className="mb-1 text-xs font-medium tracking-wider text-primary uppercase">
            Your application
          </p>
          <h1 className="font-heading text-2xl leading-tight font-semibold text-foreground">
            Welcome back
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground tabular-nums">
              {completedCount} of {totalCount}
            </span>{" "}
            steps complete at {organization.name}
          </p>
        </div>
      </div>

      {allDone && (
        <div className="mb-6 flex animate-in items-start gap-3 rounded-xl border border-success/40 bg-success-muted px-4 py-3.5 text-success-foreground shadow-[var(--shadow-card)] fade-in slide-in-from-top-2">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
            <CheckCircle2 className="size-5" />
          </div>
          <div className="text-sm">
            <p className="font-semibold text-foreground">You're all set!</p>
            <p className="text-muted-foreground">
              {organization.name} has everything they need. We'll be in touch
              shortly.
            </p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[76px] animate-pulse rounded-xl border border-border bg-muted/40"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      )}

      <div className="mt-10">
        <h2 className="mb-3 font-heading text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          More
        </h2>
        <div className="grid grid-cols-2 gap-2.5">
          <NavCard
            to="/documents"
            icon={<FileText className="size-4" />}
            label="Documents"
            description="Upload & manage"
          />
          <NavCard
            to="/appointments"
            icon={<Calendar className="size-4" />}
            label="Appointments"
            description="Schedule a visit"
          />
          <NavCard
            to="/profile"
            icon={<User className="size-4" />}
            label="Profile"
            description="Review your info"
          />
          <NavCard
            to="/help"
            icon={<HelpCircle className="size-4" />}
            label="Help"
            description="Talk to the dealer"
          />
        </div>
      </div>
    </PageShell>
  );
}

function NavCard({
  to,
  icon,
  label,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-1 rounded-xl border border-border bg-card p-3.5 shadow-[var(--shadow-card)] transition-all hover:border-ring/40 hover:shadow-[var(--shadow-card-hover)]"
    >
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        {icon}
      </span>
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </Link>
  );
}

function TaskCard({ task }: { task: Task }) {
  const taskDefinition = getTaskDefinition(task.content_type);
  const Icon = taskDefinition.icon;
  const helper = task.description ?? taskDefinition.helper;
  const closed = isClosedTask(task);

  return (
    <Link to="/steps/$taskId" params={{ taskId: task.id }} className="block">
      <div
        className={cn(
          "group/task flex items-center gap-4 rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-card)] transition-all",
          !closed &&
            "hover:border-ring/40 hover:shadow-[var(--shadow-card-hover)] active:scale-[0.995]",
          closed && "border-success/30 bg-success-muted/60",
        )}
      >
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl transition-colors",
            closed
              ? "bg-success text-success-foreground"
              : "bg-primary/10 text-primary group-hover/task:bg-primary/15",
          )}
        >
          {closed ? (
            <CheckCircle2 className="size-5" />
          ) : (
            <Icon className="size-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-foreground">{task.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {closed ? getTaskStatusLabel(task.status) : helper}
          </p>
        </div>
        <ChevronRight
          className={cn(
            "size-4 text-muted-foreground transition-transform",
            !closed && "group-hover/task:translate-x-0.5",
          )}
        />
      </div>
    </Link>
  );
}
